/**
 * useVersionCheck Hook
 * 
 * Polls /version.json every 5 minutes to detect new deployments.
 * When a new version is detected, sets `updateAvailable = true`
 * so the UI can show an update banner.
 * 
 * Also provides `forceRefresh()` which clears ALL caches
 * (service worker caches, localStorage project cache) and reloads.
 * 
 * Created to solve: users in remote locations (Tanzania) seeing
 * empty modules because their browsers cached old JS bundles.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VERSION_KEY = 'archon_build_hash';

export default function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const currentHash = useRef(localStorage.getItem(VERSION_KEY));

  const checkVersion = useCallback(async () => {
    try {
      const resp = await fetch('/version.json', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const remoteHash = data.hash;

      if (!currentHash.current) {
        // First visit — store the hash, no update needed
        currentHash.current = remoteHash;
        localStorage.setItem(VERSION_KEY, remoteHash);
        return;
      }

      if (remoteHash !== currentHash.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network error — ignore silently
    }
  }, []);

  useEffect(() => {
    // Defer first check well after first paint to avoid long tasks on startup (Violation messages)
    const t = setTimeout(checkVersion, 500);
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    return () => {
      clearTimeout(t);
      clearInterval(interval);
    };
  }, [checkVersion]);

  /**
   * Clear all caches and hard-reload the app.
   * Preserves the current route so the user lands back where they were.
   */
  const forceRefresh = useCallback(async () => {
    try {
      // 0. Save current route BEFORE anything else (sessionStorage survives reload)
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== '/' && currentPath !== '/login') {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
      }

      // 1. Clear service worker caches
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }

      // 2. Unregister service workers so they re-install fresh
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }

      // 3. Clear stale localStorage data (keep auth + tenant + cached user)
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const cachedUser = localStorage.getItem('archon_user');
      const lang = localStorage.getItem('archon_lang');
      const activeProj = localStorage.getItem('activeProject');
      localStorage.clear();
      if (token) localStorage.setItem('token', token);
      if (tenantId) localStorage.setItem('tenant_id', tenantId);
      if (cachedUser) localStorage.setItem('archon_user', cachedUser);
      if (lang) localStorage.setItem('archon_lang', lang);
      if (activeProj) localStorage.setItem('activeProject', activeProj);

      // 4. Full reload — navigate with a cache-bust param to force fresh index.html
      //    This ensures the browser fetches new JS bundles, not stale cached ones.
      const bustParam = `_v=${Date.now()}`;
      const separator = currentPath.includes('?') ? '&' : '?';
      window.location.href = currentPath + separator + bustParam;
    } catch {
      // Fallback: reload with cache bust
      window.location.href = '/?_v=' + Date.now();
    }
  }, []);

  return { updateAvailable, forceRefresh };
}
